// ServiceRequestModal — compact create modal launched from the IncidentPanel.
import React, { useEffect, useMemo, useState } from "react";
import { X, Search } from "lucide-react";
import { useCreateServiceRequest, useIncidentParticipants } from "../api/sitroom";
import { useStore } from "../store";
import {
  SR_TYPES,
  SR_TYPE_BY_ID,
  SR_PRIORITY_STYLE,
} from "../data/serviceRequestTypes";

const inputStyle = {
  width: "100%",
  background: "#08090d",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 6,
  color: "#f0f2f8",
  fontFamily: "DM Mono, monospace",
  fontSize: 12,
  padding: "8px 10px",
  outline: "none",
};

const Label = ({ children }) => (
  <div
    style={{
      fontFamily: "DM Mono, monospace",
      fontSize: 9,
      letterSpacing: "1.5px",
      color: "#9198b0",
      textTransform: "uppercase",
      marginBottom: 4,
    }}
  >
    {children}
  </div>
);

const AssigneePicker = ({ incidentId, value, onChange }) => {
  const { data: grouped = {} } = useIncidentParticipants(incidentId, "all_parties", Boolean(incidentId));
  const [search, setSearch] = useState("");
  const flat = useMemo(() => {
    const out = [];
    Object.entries(grouped).forEach(([tenant, users]) => {
      users.forEach((u) => out.push({ ...u, tenantLabel: tenant }));
    });
    return out;
  }, [grouped]);
  const filtered = flat.filter(
    (u) =>
      !search ||
      (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.tenantLabel || "").toLowerCase().includes(search.toLowerCase()),
  );
  const selected = flat.find((u) => u.id === value?.assignee_user_id);

  return (
    <div className="relative">
      <div
        className="flex items-center gap-2 rounded-md"
        style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.10)", padding: "8px 10px" }}
      >
        <Search size={11} color="#9198b0" />
        <input
          type="text"
          placeholder={selected ? selected.name : "Search users / tenants…"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 bg-transparent outline-none"
          style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#f0f2f8" }}
          data-testid="sr-modal-assignee-search"
        />
        {selected && (
          <button
            type="button"
            onClick={() => onChange({ assignee_user_id: null, assignee_tenant_id: null, assignee_name: null })}
            style={{ color: "#9aa1b3", background: "transparent", border: "none", padding: 0 }}
            aria-label="Clear assignee"
          >
            <X size={11} />
          </button>
        )}
      </div>
      {search && filtered.length > 0 && (
        <div
          className="absolute z-10 left-0 right-0 mt-1 rounded-md overflow-y-auto"
          style={{
            maxHeight: 180,
            background: "#0d0f18",
            border: "1px solid rgba(255,255,255,0.10)",
            boxShadow: "0 8px 22px rgba(0,0,0,0.5)",
          }}
          data-testid="sr-modal-assignee-results"
        >
          {filtered.slice(0, 10).map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => {
                onChange({
                  assignee_user_id: u.id,
                  assignee_tenant_id: u.tenant_id,
                  assignee_name: u.name,
                });
                setSearch("");
              }}
              className="w-full text-left px-3 py-2 hover:bg-white/[0.04]"
              data-testid={`sr-modal-assignee-option-${u.id}`}
            >
              <div style={{ fontFamily: "Syne, sans-serif", fontSize: 12, color: "#f0f2f8", fontWeight: 600 }}>
                {u.name}
              </div>
              <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9aa1b3", marginTop: 1 }}>
                {u.tenantLabel} · {u.role}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ServiceRequestModal = ({ incidentId, initialType = null, onClose }) => {
  const addToast = useStore((s) => s.addToast);
  const createMut = useCreateServiceRequest();
  const [form, setForm] = useState({
    type: initialType || "",
    title: "",
    description: "",
    assignee_user_id: null,
    assignee_tenant_id: null,
    assignee_name: null,
    priority: "medium",
    due_date: "",
  });
  const [err, setErr] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (form.type && !form.title) {
      const t = SR_TYPE_BY_ID[form.type];
      if (t) update("title", `${t.labelEn} — `);
    }
     
  }, [form.type]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.type) {
      setErr("Pick a service request type");
      return;
    }
    if ((form.title || "").trim().length < 3) {
      setErr("Title must be at least 3 characters");
      return;
    }
    try {
      const body = {
        type: form.type,
        title: form.title.trim(),
        description: form.description.trim() || null,
        assignee_user_id: form.assignee_user_id,
        assignee_tenant_id: form.assignee_tenant_id,
        assignee_name: form.assignee_name,
        priority: form.priority,
        due_date: form.due_date || null,
      };
      const created = await createMut.mutateAsync({ incidentId, body });
      addToast({
        type: "info",
        title: "Service request created",
        body: `${created.type} · ${form.assignee_name || "Unassigned"}${form.assignee_user_id ? " notified" : ""}`,
      });
      onClose();
    } catch (ex) {
      const detail = ex?.response?.data?.detail || ex.message || "Create failed";
      setErr(typeof detail === "string" ? detail : JSON.stringify(detail));
    }
  };

  return (
    <div
      className="fixed inset-0 z-[400] flex items-start justify-center px-4 py-12 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="sr-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="rounded-lg w-full"
        style={{
          maxWidth: 620,
          background: "#0d0f18",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        >
          <h2
            style={{
              fontFamily: "Syne, sans-serif",
              fontWeight: 800,
              fontSize: 17,
              color: "#f0f2f8",
              letterSpacing: "-0.2px",
            }}
          >
            New Service Request
          </h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 hover:bg-white/5" aria-label="Close">
            <X size={14} color="#9198b0" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Type grid */}
          <div>
            <Label>Type</Label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {SR_TYPES.map((t) => {
                const Icon = t.Icon;
                const active = form.type === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => update("type", t.id)}
                    className="flex flex-col items-center gap-1.5 rounded-md p-2 transition-colors"
                    style={{
                      background: active ? `${t.color}1f` : "#08090d",
                      border: `1px solid ${active ? t.color : "rgba(255,255,255,0.08)"}`,
                      color: active ? t.color : "#cfd1d8",
                      cursor: "pointer",
                    }}
                    data-testid={`sr-modal-type-${t.id}`}
                    aria-pressed={active}
                  >
                    <Icon size={16} />
                    <span
                      style={{
                        fontFamily: "DM Mono, monospace",
                        fontSize: 9,
                        letterSpacing: "0.5px",
                        textAlign: "center",
                        lineHeight: 1.2,
                      }}
                    >
                      {t.labelEn}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Title</Label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Short summary of the request…"
              style={inputStyle}
              data-testid="sr-modal-title"
            />
          </div>

          <div>
            <Label>Description (optional)</Label>
            <textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={3}
              placeholder="Additional context, deliverables, constraints…"
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.55 }}
              data-testid="sr-modal-description"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Assignee</Label>
              <AssigneePicker incidentId={incidentId} value={form} onChange={(p) => setForm((f) => ({ ...f, ...p }))} />
              {form.assignee_name && (
                <div
                  className="mt-1.5"
                  style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9ec3ec", letterSpacing: "0.3px" }}
                  data-testid="sr-modal-selected-assignee"
                >
                  → {form.assignee_name}
                </div>
              )}
            </div>
            <div>
              <Label>Priority</Label>
              <div className="flex gap-2">
                {Object.entries(SR_PRIORITY_STYLE).map(([id, p]) => {
                  const active = form.priority === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => update("priority", id)}
                      className="flex-1 rounded-md py-2"
                      style={{
                        background: active ? `${p.color}1f` : "#08090d",
                        border: `1px solid ${active ? p.color : "rgba(255,255,255,0.10)"}`,
                        color: active ? p.color : "#9aa1b3",
                        fontFamily: "DM Mono, monospace",
                        fontSize: 11,
                        letterSpacing: "0.5px",
                        cursor: "pointer",
                      }}
                      data-testid={`sr-modal-priority-${id}`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <Label>Due date (optional)</Label>
            <input
              type="datetime-local"
              value={form.due_date}
              onChange={(e) => update("due_date", e.target.value)}
              style={inputStyle}
              data-testid="sr-modal-due-date"
            />
          </div>

          {err && (
            <div
              className="rounded-md px-3 py-2"
              style={{
                background: "rgba(226,75,74,0.10)",
                border: "1px solid rgba(226,75,74,0.45)",
                color: "#ff7e7c",
                fontFamily: "DM Mono, monospace",
                fontSize: 10,
              }}
              data-testid="sr-modal-error"
            >
              {err}
            </div>
          )}
        </div>

        <div
          className="flex items-center justify-end gap-2 px-5 py-3"
          style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-md"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "#cfd1d8",
              fontFamily: "Syne, sans-serif",
              fontWeight: 600,
              fontSize: 12,
            }}
            data-testid="sr-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMut.isPending}
            className="px-3 py-2 rounded-md"
            style={{
              background: "#185FA5",
              color: "#fff",
              fontFamily: "Syne, sans-serif",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: "0.4px",
              opacity: createMut.isPending ? 0.6 : 1,
            }}
            data-testid="sr-modal-submit"
          >
            {createMut.isPending ? "Creating…" : "Create Service Request"}
          </button>
        </div>
      </form>
    </div>
  );
};
